/* eslint-disable react/react-in-jsx-scope */
import { history } from 'duck/store'
import { TemporalEntityId } from 'entities-next/common'
import { isEmpty } from 'lodash'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { LoadingScreen } from 'scenes/Dashboard/components-next/Loading'
import { EntityId } from 'scenes/Dashboard/data/common/types'
import { ExtendableStyles, Testable } from 'utils-next/types'
import { emptyStringPropertiesToNull } from 'utils/types'

import {
  ApiCreateScoutingCampaignData,
  apiUpdateScoutingCampaignAsAdmin,
  getApiScoutingCampaignHandlers,
  PatchScoutingCampaign,
  SCOUTING_CAMPAIGN_CACHE_STORE_ID,
} from '../../../../../../../../entities-next/scoutingCampaign'
import {
  getApiScoutingCampaignCriteriaHandlers,
  NegativeOrganizationHeadquarters,
  OrganizationFoundedAt,
  OrganizationHeadquarters,
  OrganizationStages,
} from '../../../../../../../../entities-next/scoutingCampaignCriteria'
import { SearchFunctionType } from '../../../../../../../../entities-next/searchFunctions'
import { ScoutingCampaignDataForm } from '../../components'
import { getScoutingCampaignDetailsFormDataPath } from '../../ScoutingCampaignDetailsView.routes'
import { useScoutingCampaignDetailsViewStore } from '../../ScoutingCampaignDetailsView.store'
import {
  failedScoutingCampaignOperationAction,
  successfulScoutingCampaignCreationAction,
  successfulScoutingCampaignUpdateAction,
} from './ScoutingCampaignDetailsFormViewAlerts.component'

export type ScoutingCampaignDetailsFormViewProps = ExtendableStyles & Testable

export function ScoutingCampaignDetailsFormView({
  className,
  style,
  ...otherProps
}: ScoutingCampaignDetailsFormViewProps) {
  const {
    state: { scoutingCampaign, status, actionBarRef },
  } = useScoutingCampaignDetailsViewStore()
  const { updateOrCreateScoutingCampaign } = useScoutingCampaignDetailsFormViewHandlers()

  if (!scoutingCampaign || status === 'loading') {
    return <LoadingScreen />
  }

  return (
    <ScoutingCampaignDataForm
      scoutingCampaign={scoutingCampaign}
      onSubmit={updateOrCreateScoutingCampaign}
      actionBarRef={actionBarRef}
      {...otherProps}
    />
  )
}

function useScoutingCampaignDetailsFormViewHandlers() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const updateOrCreateScoutingCampaign = useCallback(
    async (scoutingCampaignId: EntityId, apiData: ApiCreateScoutingCampaignData) => {
      const { apiCreateScoutingCampaign } = getApiScoutingCampaignHandlers()
      const { apiSaveScoutingCampaignCriteria } = getApiScoutingCampaignCriteriaHandlers()
      try {
        let scoutingCampaign
        if (scoutingCampaignId === TemporalEntityId.new) {
          const data = {
            ...apiData,
            type: SearchFunctionType.sprintScouting,
          }
          scoutingCampaign = await apiCreateScoutingCampaign(data)
          dispatch(successfulScoutingCampaignCreationAction(apiData))
        } else {
          const updateScoutingCampaign = emptyStringPropertiesToNull(apiData) as PatchScoutingCampaign
          scoutingCampaign = await apiUpdateScoutingCampaignAsAdmin(scoutingCampaignId, updateScoutingCampaign)
          dispatch(successfulScoutingCampaignUpdateAction(apiData))
        }
        const criteria = {
          negativeOrganizationHeadquarters: !isEmpty(apiData.negativeOrganizationHeadquarters)
            ? (apiData.negativeOrganizationHeadquarters as NegativeOrganizationHeadquarters)
            : undefined,
          organizationHeadquarters: !isEmpty(apiData.organizationHeadquarters)
            ? (apiData.organizationHeadquarters as OrganizationHeadquarters)
            : undefined,
          organizationFoundedAt: !isEmpty(apiData.organizationFoundedAt)
            ? (apiData.organizationFoundedAt as OrganizationFoundedAt)
            : undefined,
          organizationStages: !isEmpty(apiData.organizationStages)
            ? (apiData.organizationStages as OrganizationStages)
            : undefined,
        }
        await apiSaveScoutingCampaignCriteria(scoutingCampaign.id, criteria)

        history.push(getScoutingCampaignDetailsFormDataPath(scoutingCampaign.id))
        queryClient.refetchQueries(SCOUTING_CAMPAIGN_CACHE_STORE_ID)
      } catch (e) {
        dispatch(failedScoutingCampaignOperationAction(e))
      }
    },
    [dispatch]
  )

  return {
    updateOrCreateScoutingCampaign,
  }
}
